import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CardMenuButton } from './CardMenuButton';
import styles from './CardMenu.module.scss';

const MENU_OFFSET_TOP = 8;

// `position: fixed; right` is measured from the viewport minus any scrollbar
// gutter — html has `scrollbar-gutter: stable` (global.scss), so on classic
// scrollbars window.innerWidth overshoots by the gutter width. clientWidth
// excludes the gutter and, unlike html's bounding rect, doesn't shift with
// horizontal page scroll.
const rightOffsetFrom = (rect: DOMRect) => document.documentElement.clientWidth - rect.right;

const MENU_ANIMATION = {
  initial: { opacity: 0, y: -6, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -6, scale: 0.96 },
  transition: { type: 'spring', stiffness: 400, damping: 28 },
} as const;

export interface CardMenuItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
}

export interface CardMenuProps {
  items: CardMenuItem[];
  ariaLabel?: string;
}

export const CardMenu: React.FC<CardMenuProps> = ({ items, ariaLabel = 'More actions' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + MENU_OFFSET_TOP,
      right: rightOffsetFrom(rect),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let frame: number;
    const track = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (rect) {
        const next = { top: rect.bottom + MENU_OFFSET_TOP, right: rightOffsetFrom(rect) };
        setPosition((prev) => (prev && prev.top === next.top && prev.right === next.right ? prev : next));
      }
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuContainerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <CardMenuButton
        ariaLabel={ariaLabel}
        expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      />

      {createPortal(
        <AnimatePresence>
          {open && position && (
            <motion.div
              className={styles.menu}
              ref={menuContainerRef}
              style={{ top: position.top, right: position.right }}
              {...MENU_ANIMATION}
            >
              <div className={styles.items}>
                {items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={styles.item}
                    onClick={() => {
                      setOpen(false);
                      item.onClick();
                    }}
                  >
                    {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default CardMenu;
